import { useMemo } from "react";
import { Link } from "react-router-dom";
import Container from "@/components/base/Container";
import SectionHeading from "@/components/feature/SectionHeading";
import FilmCard from "@/components/feature/FilmCard";
import NewsCard from "@/components/feature/NewsCard";
import HeroBanner from "@/pages/home/components/HeroBanner";
import CategoryShortcuts from "@/pages/home/components/CategoryShortcuts";
import HomeSidebar from "@/pages/home/components/HomeSidebar";
import { films } from "@/mocks/films";
import { news } from "@/mocks/news";
import { topics, heroSlides } from "@/mocks/topics";
import type { Film, NewsArticle, Topic, HeroSlide } from "@/types/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function Home() {
  useDocumentMeta(
    "夜勤病栋影视大全－最新电影电视剧资讯与剧情介绍",
    "夜勤病栋网站提供丰富的影视内容，包括电影、电视剧的最新资讯、剧情介绍和专题分析，满足用户对夜勤病栋相关影视作品的全面了解需求。"
  );

  const filmList = films as Film[];
  const newsList = news as NewsArticle[];
  const topicList = topics as Topic[];
  const slideList = heroSlides as HeroSlide[];

  const latestNews = useMemo(
    () => [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 5),
    [newsList]
  );

  const popularFilms = useMemo(
    () => [...filmList].sort((a, b) => b.views - a.views).slice(0, 8),
    [filmList]
  );

  const featuredTopics = topicList.slice(0, 3);

  return (
    <div className="flex flex-col">
      <HeroBanner slides={slideList} />

      <Container className="-mt-8 md:-mt-10 relative z-10">
        <CategoryShortcuts />
      </Container>

      <Container className="mt-10 md:mt-14">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
          <div className="flex-1 min-w-0">
            <section>
              <SectionHeading
                title="最新资讯"
                subtitle="影视快讯、剧情解析与专题长文，持续更新"
                moreTo="/search?category=资讯"
                moreLabel="查看全部"
              />
              {latestNews.length > 0 ? (
                <div className="space-y-4">
                  {latestNews.map((article) => (
                    <NewsCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <p className="py-16 text-center text-foreground-500 border border-dashed border-background-300 rounded-lg">
                  暂无最新资讯
                </p>
              )}
            </section>

            <section className="mt-12 md:mt-14">
              <SectionHeading
                title="热门影视推荐"
                subtitle="按站内浏览量排序的夜勤题材作品"
                moreTo="/category"
                moreLabel="进入分类"
              />
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                {popularFilms.map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
              </div>
            </section>
          </div>

          <HomeSidebar topics={topicList} popular={popularFilms.slice(0, 5)} />
        </div>
      </Container>

      <Container className="mt-12 md:mt-16 mb-12 md:mb-16">
        <SectionHeading
          title="专题精选"
          subtitle="围绕夜勤题材的深度主题内容"
          moreTo="/topic"
          moreLabel="全部专题"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {featuredTopics.map((topic) => (
            <Link
              key={topic.id}
              to={`/topic?focus=${topic.id}`}
              className="group relative rounded-lg overflow-hidden border border-background-200"
            >
              <div className="relative w-full h-52 md:h-56">
                <img
                  src={topic.cover}
                  alt={`夜勤病栋影视专题：${topic.title}`}
                  title={`${topic.title} - 夜勤病栋影视专题`}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/40 to-transparent"></div>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-5">
                <span className="inline-block px-2 py-0.5 rounded bg-accent-500 text-background-50 text-[11px] mb-2">
                  专题
                </span>
                <h3 className="font-heading font-bold text-background-50 text-base md:text-lg leading-snug">
                  {topic.title}
                </h3>
                <p className="mt-1.5 text-xs text-background-200 line-clamp-2 leading-relaxed">
                  {topic.subtitle}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </Container>
    </div>
  );
}